import ExcelJS from 'exceljs';
import { LastCargoesReportData } from '../ReportGeneratorTypes';
import Decimal from 'decimal.js';
import { generateReportHeaderExcel } from './reportHeader';
import { getCoatingName } from 'src/utils/stowage/tankUtils';
import { getFormattedFillRatio, getLetterForNumber, getOrdinalSuffix, setCargoColor, setHeaderStyle } from '../excelReportUtils';
import { getCargoFosfaCellStatus } from 'src/utils/stowage/tankPlanFilterUtils';
import sortUtils from 'src/utils/sortUtils';

const DATA_START_ROW = 7;

/**
 * Generates an Excel report showing the last cargoes for each tank
 * @param workbook - ExcelJS workbook instance
 * @param data - Report data containing tank cargo history information
 * @param logoImageId - ID of the logo image in the workbook
 * @param fosfaMode - Controls FOSFA-related filtering/coloring:
 *   - 'off' (default): no coloring, no filtering (plain Last Cargoes report).
 *   - 'approved': Last Cargo - Approved behavior. Only tanks whose immediate previous cargo is
 *     'acceptable' are included (unless includeAllTanks). Green is applied to the immediate
 *     previous cargo column only when 'acceptable'; red is applied to the 2nd/3rd previous cargo
 *     columns only when 'banned'.
 *   - 'nobl': Last Cargo - NOBL behavior. Tanks are excluded only when their immediate previous
 *     cargo is 'banned' (unless includeAllTanks) - 'acceptable' and 'none' are both kept. The
 *     immediate previous cargo column is colored green when 'acceptable', red when 'banned' (only
 *     reachable when includeAllTanks bypasses the filter), and left uncolored when 'none'. The
 *     2nd/3rd previous cargo columns behave the same as 'approved' (red when 'banned').
 * @param includeUnNo - When true, includes the UN No. column for each cargo group. Defaults to false.
 * @param includeAllTanks - When false (default) and fosfaMode is 'approved' or 'nobl', tanks are
 *   filtered per the rules above. When true, all tanks are included regardless of FOSFA status.
 *   Has no effect when fosfaMode is 'off'.
 */
export function generateLastCargoesReportExcel(
    workbook: ExcelJS.Workbook,
    data: LastCargoesReportData,
    logoImageId: number,
    fosfaMode: 'off' | 'approved' | 'nobl' = 'off',
    includeUnNo = false,
    includeAllTanks = false
): void {
    const { numberOfSteps, selectedBerthCall, cargoNameType } = data;
    const _columnsPerCargo = includeUnNo ? 3 : 2;

    const _worksheet = workbook.addWorksheet('Last Cargoes');

    let _title = `${numberOfSteps} Last Cargoes in ${data.selectedPortCall?.voyageNumber} ${data.selectedPortCall?.portDisplayName ?? ''}`;
    if (selectedBerthCall && selectedBerthCall.berthName) {
        _title += ` - ${selectedBerthCall.berthName}`;
    }


    generateReportHeaderExcel(_worksheet, data.vessel.vesselName, _title, data.selectedCharterers, logoImageId, 11);

    let currentRow = DATA_START_ROW;

    const _headerRow = _worksheet.getRow(currentRow);
    const _subHeaderRow = _worksheet.getRow(currentRow + 1);

    _headerRow.height = 20;
    _subHeaderRow.height = 20;
    let _columnIndex = 1;

    // First header group - tank information
    const tankHeaderCell = _headerRow.getCell(_columnIndex);
    tankHeaderCell.value = 'Tank';
    tankHeaderCell.style = {
        alignment: {
            vertical: 'middle',
            horizontal: 'center',
        },
    };
    _worksheet.mergeCells(currentRow, _columnIndex, currentRow + 1, _columnIndex);
    _columnIndex++;

    const coatingHeaderCell = _headerRow.getCell(_columnIndex);
    coatingHeaderCell.value = 'Coating';
    coatingHeaderCell.style = {
        alignment: {
            vertical: 'middle',
            horizontal: 'center',
        },
    };
    _worksheet.mergeCells(currentRow, _columnIndex, currentRow + 1, _columnIndex);
    _columnIndex++;

    const volumeHeaderCell = _headerRow.getCell(_columnIndex);
    volumeHeaderCell.value = '98% Vol.';
    volumeHeaderCell.style = {
        alignment: {
            vertical: 'middle',
            horizontal: 'center',
        },
    };
    _worksheet.mergeCells(currentRow, _columnIndex, currentRow + 1, _columnIndex);
    _columnIndex++;

    // Apply border after base columns
    for (let i = 1; i <= _columnIndex - 1; i++) {
        setHeaderStyle(_headerRow.getCell(i));
        setHeaderStyle(_subHeaderRow.getCell(i));
    }

    // Add right border to the last base column
    _headerRow.getCell(_columnIndex - 1).border = {
        ..._headerRow.getCell(_columnIndex - 1).border,
        right: { style: 'medium' },
    };
    _subHeaderRow.getCell(_columnIndex - 1).border = {
        ..._subHeaderRow.getCell(_columnIndex - 1).border,
        right: { style: 'medium' },
    };

    // Add cargo column groups - each cargo has 2 columns (Trade Name, Fill ratio), or 3 when UN No. is included
    for (let i = 0; i < numberOfSteps; i++) {
        const _cargoHeader = i === 0 ? 'Previous Cargo' : `${i + 1}${getOrdinalSuffix(i + 1)} Previous Cargo`;
        const _lastColumnOffset = _columnsPerCargo - 1;

        // Merge cargo header cells
        _worksheet.mergeCells(currentRow, _columnIndex, currentRow, _columnIndex + _lastColumnOffset);
        _headerRow.getCell(_columnIndex).value = _cargoHeader;

        // Set subheaders
        _subHeaderRow.getCell(_columnIndex).value = cargoNameType === 'tradeName' ? 'Trade Name' : 'Product Name';
        if (includeUnNo) {
            _subHeaderRow.getCell(_columnIndex + 1).value = 'UN No.';
        }
        _subHeaderRow.getCell(_columnIndex + _lastColumnOffset).value = 'Fill ratio';

        // Style cargo headers
        for (let j = 0; j < _columnsPerCargo; j++) {
            setHeaderStyle(_headerRow.getCell(_columnIndex + j));
            setHeaderStyle(_subHeaderRow.getCell(_columnIndex + j));
        }

        // Add right border to the last column in this cargo group
        _headerRow.getCell(_columnIndex + _lastColumnOffset).border = {
            ..._headerRow.getCell(_columnIndex + _lastColumnOffset).border,
            right: { style: 'medium' },
        };
        _subHeaderRow.getCell(_columnIndex + _lastColumnOffset).border = {
            ..._subHeaderRow.getCell(_columnIndex + _lastColumnOffset).border,
            right: { style: 'medium' },
        };

        _columnIndex += _columnsPerCargo;
    }

    // Move to data rows (after header and subheader)
    currentRow += 2;

    // Determines whether a tank's immediate previous cargo keeps it in the report, per fosfaMode:
    // 'approved' keeps only 'acceptable' cargoes; 'nobl' excludes only 'banned' cargoes (keeping
    // both 'acceptable' and 'none').
    const passesFosfaFilter = (tank: (typeof data.vessel.cargoTanks)[number]): boolean => {
        const _cargoContents = data.cargoContentsPerTank.find((_cargoContent) => _cargoContent.tankId === tank.tankId);
        const _cargo = _cargoContents?.cargoContent[0];
        if (!_cargo) {
            return false;
        }

        const weight = _cargo.weight instanceof Decimal ? _cargo.weight : new Decimal(_cargo.weight ?? 0);
        const fillRatio = weight.gt(0) ? getFormattedFillRatio(weight, data.stowagePlan, _cargo, tank) : null;
        const fosfaStatus = getCargoFosfaCellStatus(_cargo, fillRatio);

        return fosfaMode === 'nobl' ? fosfaStatus !== 'banned' : fosfaStatus === 'acceptable';
    };

    let tanks = data.selectedTankIds
        ? data.vessel.cargoTanks.filter((tank) => data.selectedTankIds?.includes(tank.tankId))
        : data.vessel.cargoTanks;

    // By default, when generating an 'approved' or 'nobl' report, only include tanks whose
    // immediate previous cargo passes the mode's filter. Selecting "Include all tanks" bypasses this.
    if (fosfaMode !== 'off' && !includeAllTanks) {
        tanks = tanks.filter((tank) => passesFosfaFilter(tank));
    }

    const { compareTankNamesForLastCargoes } = sortUtils();

    // Add tank data rows
    for (const _tank of tanks.sort((a, b) => compareTankNamesForLastCargoes(a.tankName, b.tankName))) {
        const _dataRow = _worksheet.getRow(currentRow);
        _dataRow.height = 16;

        const coatingName = getCoatingName(_tank.coatingType);

        _dataRow.getCell(1).value = _tank.tankName;
        _dataRow.getCell(2).value = coatingName ?? '';
        _dataRow.getCell(3).value = _tank.volumeAt98Percent;

        // Add right border to the last base column
        _dataRow.getCell(3).border = {
            ..._dataRow.getCell(3).border,
            right: { style: 'medium' },
        };

        // set value format of cell 3 to number
        _dataRow.getCell(3).numFmt = '#,##0.00';

        const _cargoContents = data.cargoContentsPerTank.find((_cargoContent) => _cargoContent.tankId === _tank.tankId);

        // Add cargo data
        let _colIndex = 4;
        for (let i = 0; i < numberOfSteps; i++) {
            const _cargo = _cargoContents?.cargoContent[i];
            const _fillRatioColIndex = _colIndex + _columnsPerCargo - 1;

            if (_cargo) {
                const weight = _cargo.weight instanceof Decimal ? _cargo.weight : new Decimal(_cargo.weight ?? 0);
                const fillRatio = weight.gt(0) ? getFormattedFillRatio(weight, data.stowagePlan, _cargo, _tank) : null;

                _dataRow.getCell(_colIndex).value =
                    cargoNameType === 'tradeName' ? _cargo.cargo?.product?.tradeName : _cargo.cargo?.product?.category?.name;
                if (includeUnNo) {
                    _dataRow.getCell(_colIndex + 1).value = _cargo.cargo.product.unCode; // Product UN No.
                }
                _dataRow.getCell(_fillRatioColIndex).value = fillRatio !== null ? fillRatio : '';
                _dataRow.getCell(_fillRatioColIndex).numFmt = '0.00%';

                if (fosfaMode !== 'off') {
                    const fosfaStatus = getCargoFosfaCellStatus(_cargo, fillRatio);
                    // Green (acceptable) only applies to the immediate previous cargo (i === 0).
                    // Red (banned) only applies to the 2nd and 3rd previous cargo (i === 1 or i === 2)
                    // for both modes; for the immediate previous cargo (i === 0), red also applies
                    // in 'nobl' mode when the tank was kept via "Include all tanks" despite being banned.
                    if (i === 0 && fosfaStatus === 'acceptable') {
                        setCargoColor(_dataRow.getCell(_colIndex), 'C6EFCE');
                    } else if (i === 0 && fosfaMode === 'nobl' && fosfaStatus === 'banned') {
                        setCargoColor(_dataRow.getCell(_colIndex), 'FFC7CE');
                    } else if ((i === 1 || i === 2) && fosfaStatus === 'banned') {
                        setCargoColor(_dataRow.getCell(_colIndex), 'FFC7CE');
                    }
                }
            } else {
                _dataRow.getCell(_colIndex).value = '';
                if (includeUnNo) {
                    _dataRow.getCell(_colIndex + 1).value = '';
                }
                _dataRow.getCell(_fillRatioColIndex).value = '';
            }

            // Add right border to the last column in each cargo group
            _dataRow.getCell(_fillRatioColIndex).border = {
                ..._dataRow.getCell(_fillRatioColIndex).border,
                right: { style: 'medium' },
            };

            _colIndex += _columnsPerCargo;
        }

        currentRow++;
    }

    // Set column widths
    _worksheet.getColumn(1).width = 10; // Tank
    _worksheet.getColumn(2).width = 10; // Coating
    _worksheet.getColumn(3).width = 10; // 98% Vol

    // Set cargo column widths
    for (let i = 0; i < numberOfSteps; i++) {
        _worksheet.getColumn(4 + i * _columnsPerCargo).width = 30; // Trade Name
        if (includeUnNo) {
            _worksheet.getColumn(5 + i * _columnsPerCargo).width = 10; // UN No.
        }
        _worksheet.getColumn(4 + i * _columnsPerCargo + (_columnsPerCargo - 1)).width = 10; // Fill ratio
    }

    // Add borders to table
    const _lastRow = currentRow - 1;
    for (let i = DATA_START_ROW; i <= _lastRow; i++) {
        for (let j = 0; j < _columnIndex - 1; j++) {
            _worksheet.getCell(`${getLetterForNumber(j)}${i}`).border = {
                ..._worksheet.getCell(`${getLetterForNumber(j)}${i}`).border,
                top: { style: 'thin' },
                bottom: { style: 'thin' },
            };
        }
    }

    // Add bottom border to the last row
    for (let j = 0; j < _columnIndex - 1; j++) {
        _worksheet.getCell(`${getLetterForNumber(j)}${_lastRow}`).border = {
            ..._worksheet.getCell(`${getLetterForNumber(j)}${_lastRow}`).border,
            bottom: { style: 'medium' },
        };
    }

    _worksheet.views = [
        { state: 'frozen', xSplit: 0, ySplit: DATA_START_ROW + 1, topLeftCell: `A${DATA_START_ROW + 2}`, activeCell: `A${DATA_START_ROW + 2}` },
    ];
}
